import React, { createContext, useContext, useCallback, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface NavigationContextType {
  // Core navigation
  navigateTo: (path: string) => void;
  navigateToLead: (leadId: string) => void;
  navigateToClient: (clientId: string) => void;

  // Omnichannel navigation
  navigateToOmnichannel: (entityId: string, channel?: 'chat' | 'whatsapp' | 'email' | 'call' | 'video') => void;

  // Calendar navigation
  navigateToCalendar: (entityId?: string) => void;

  // Current location helpers
  currentPath: string;
  isOnPage: (path: string) => boolean;

  // Go back
  goBack: () => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export function NavigationProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  const navigateTo = useCallback((path: string) => {
    navigate(path);
  }, [navigate]);

  const navigateToLead = useCallback((leadId: string) => {
    navigate(`/leads/${leadId}`);
  }, [navigate]);

  const navigateToClient = useCallback((clientId: string) => {
    navigate(`/clients/${clientId}`);
  }, [navigate]);

  const navigateToOmnichannel = useCallback((entityId: string, channel: 'chat' | 'whatsapp' | 'email' | 'call' | 'video' = 'chat') => {
    const params = new URLSearchParams({
      id: entityId,
      channel: channel,
    });
    navigate(`/omnichannel?${params.toString()}`);
  }, [navigate]);

  const navigateToCalendar = useCallback((entityId?: string) => {
    if (entityId) {
      navigate(`/calendar?leadId=${entityId}`);
    } else {
      navigate('/calendar');
    }
  }, [navigate]);

  const isOnPage = useCallback((path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  }, [location.pathname]);

  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const value: NavigationContextType = {
    navigateTo,
    navigateToLead,
    navigateToClient,
    navigateToOmnichannel,
    navigateToCalendar,
    currentPath: location.pathname,
    isOnPage,
    goBack,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
}
